import { useSyncExternalStore } from 'react'

// A module-level store rather than React state, so api/client.ts can read the token on every request.
const STORAGE_KEY = 'cockpit.auth.session'

export interface Session {
  email: string
  name: string
  /** Unix seconds. Google ID tokens live about an hour. */
  exp: number
  /** The raw ID token, sent as the bearer token. */
  credential: string
}

type Listener = () => void

const listeners = new Set<Listener>()
let session: Session | null = readStoredSession()

function readStoredSession(): Session | null {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'null') as Partial<Session> | null
    if (!stored?.credential || !stored.email || !stored.exp) return null
    // A cheap pre-flight only; the API checks the real lifetime.
    return stored.exp * 1000 > Date.now() ? (stored as Session) : null
  } catch {
    return null
  }
}

export const getSession = () => session

export function setSession(next: Session | null): void {
  session = next
  try {
    if (next) localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
    else localStorage.removeItem(STORAGE_KEY)
  } catch {
    // Blocked site data: keep the in-memory session.
  }
  listeners.forEach((listener) => listener())
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export const useSession = () => useSyncExternalStore(subscribe, getSession)

export function signOut(): void {
  // Stops One Tap from signing the same account straight back in.
  window.google?.accounts.id.disableAutoSelect()
  setSession(null)
}
